import { notificationAPI } from 'api/NotificationAPI'
import { useEffect, useState, useRef, useCallback } from 'react'
import { IError } from 'types/error'
import { useUser } from './user'
import { INotification } from 'types/notificationInterfaces'

const CACHE_DURATION = 30000 // 30 seconds

// Shared cache outside component (read-only during render)
let notificationCache: { data: INotification[]; timestamp: number } | null = null

/**
 * Hook for getting user notifications.
 *
 * @returns Hook variables and functions.
 */
export const useNotifications = () => {
  // Only read cache during initialization, never mutate during render
  const [notifications, setNotifications] = useState<INotification[]>(() => {
    if (notificationCache && Date.now() - notificationCache.timestamp < CACHE_DURATION) {
      return notificationCache.data
    }
    return []
  })
  const [unreadCount, setUnreadCount] = useState<number>(() => {
    if (notificationCache && Date.now() - notificationCache.timestamp < CACHE_DURATION) {
      return notificationCache.data.filter(notification => notification.key && notification.key.includes('unread'))
        .length
    }
    return 0
  })
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [error, setError] = useState<IError>()
  const fetchingRef = useRef(false)

  const { user } = useUser()

  useEffect(() => {
    const controller = new AbortController()

    // Cache is read during initialization above; we always attempt a
    // background refresh when a user exists so errors and fresh data are
    // surfaced even if cached data was returned on first render.
    ;(async () => {
      // If there's a user, always attempt a fetch (even when cache exists) so
      // callers get fresh data and errors are surfaced. We still use the cache
      // for the initial render and avoid flipping any loading indicator when
      // cached data is present.
      if (user && !fetchingRef.current) {
        fetchingRef.current = true
        try {
          const data = await notificationAPI.getNotifications(controller)

          setNotifications(data)
          setUnreadCount(data.filter(notification => notification.key && notification.key.includes('unread')).length)

          // Update cache inside effect (side-effect zone, not during render)
          notificationCache = {
            data,
            timestamp: Date.now(),
          }
        } catch (error) {
          if ((error as IError).name !== 'CanceledError' && (error as IError).name !== 'AbortError') {
            setError(error as IError)
          }
        } finally {
          fetchingRef.current = false
        }
      }
    })()

    return () => {
      controller.abort()
      fetchingRef.current = false
    }
  }, [user, refreshTrigger])

  const setRefresh = useCallback(() => {
    // Clear cache and trigger refetch
    notificationCache = null
    setRefreshTrigger(prev => prev + 1)
  }, [])

  return {
    notifications,
    unreadCount,
    setRefresh,
    error,
  }
}
